import { Link } from 'react-router';
import { SEO } from '@/components/seo/seo';
import { COOKIE_SETTINGS_EVENT } from '@/components/privacy/cookie-consent';

export function CookiesPage() {
  return (
    <div className="relative">
      <SEO
        title="Cookie Policy"
        description="How Fixtyer uses essential storage, Google Consent Mode and optional analytics and advertising storage."
        canonical="/cookies"
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        <div className="absolute inset-x-0 top-0 h-8 bg-[repeating-linear-gradient(-45deg,#f97316,#f97316_40px,#1e40af_40px,#1e40af_80px)]" />
        <div className="relative mx-auto max-w-4xl px-4 pt-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center gap-2 text-sm text-brand-600">
            <Link to="/" className="hover:text-brand-700">Home</Link>
            <span aria-hidden="true">/</span>
            <span className="text-surface-700">Cookie Policy</span>
          </div>
          <h1 className="font-display text-4xl font-bold text-brand-950 sm:text-5xl">Cookie Policy</h1>
          <p className="mt-4 text-lg text-surface-700">Last updated: 19 July 2026</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10 text-surface-700">
            <PolicySection title="Your choice">
              <p>
                On your first visit, Fixtyer asks you to choose <strong>Accept analytics and advertising</strong> or{' '}
                <strong>Essential only</strong>. We save that choice in your browser&apos;s local storage
                under <code>fixtyer_cookie_consent</code>. Essential product storage remains available
                whichever option you choose.
              </p>
              <p>
                You can change your choice at any time using the Cookie Settings button below or in the
                footer. Changing to Essential only sends an updated denied consent signal to Google.
              </p>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))}
                className="mt-2 rounded-lg bg-brand-600 px-5 py-3 font-semibold text-white hover:bg-brand-700"
              >
                Open Cookie Settings
              </button>
            </PolicySection>

            <PolicySection title="Essential storage">
              <p>
                Fixtyer uses browser storage needed to provide the service, including account
                authentication, security, claim preparation state and your cookie preference. This
                storage is not switched off by the consent control because the product cannot provide
                those requested functions without it.
              </p>
              <p>
                Stripe may use storage necessary for fraud prevention and payment processing when you
                choose to buy the currently available £35 Council Claim Pack. See{' '}
                <a
                  href="https://stripe.com/gb/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 underline"
                >
                  Stripe&apos;s privacy policy
                </a>.
              </p>
            </PolicySection>

            <PolicySection title="Google Analytics and advertising storage">
              <p>
                The Google tag loads with Google Consent Mode v2 defaults set to denied for{' '}
                <code>analytics_storage</code>, <code>ad_storage</code>, <code>ad_user_data</code> and{' '}
                <code>ad_personalization</code>. These signals are set before Google Analytics is
                configured.
              </p>
              <p>
                If you choose Essential only, those four consent signals stay denied. Google is not
                permitted to read or write analytics or advertising storage, although Google Consent
                Mode may send cookieless consent-status and measurement pings.
              </p>
              <p>
                If you choose Accept analytics and advertising, Fixtyer updates all four signals to granted. Google may
                then use analytics and advertising storage to measure site use and campaign performance.
                We do not grant those signals before you accept.
              </p>
              <p>
                Fixtyer creates an anonymous, session-only journey identifier in browser session storage
                so first-touch pages and claim-preparation steps can be connected without names, contact
                details or claim contents. It is sent to Google only after analytics consent and disappears
                when the browser session ends. Actions taken before consent are not replayed later.
              </p>
              <p>
                Google Analytics commonly uses cookies such as <code>_ga</code> and a property-specific{' '}
                <code>_ga_*</code> cookie after consent.
                See{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 underline"
                >
                  Google&apos;s privacy policy
                </a>.
              </p>
            </PolicySection>

            <PolicySection title="Storage retention">
              <p>
                The <code>fixtyer_cookie_consent</code> preference remains in local storage until you change
                your choice or clear browser storage. It has no separate automatic expiry.
              </p>
              <p>
                After consent, Google states that <code>_ga</code> and <code>_ga_*</code> cookies commonly
                remain for up to two years and may be refreshed when the site is used. Google controls the
                precise duration, and its practices can change.
              </p>
              <p>
                Essential authentication, security and claim-preparation storage is retained according to
                its function and the account or document retention periods in our Privacy Policy. Stripe
                controls the retention of storage it uses for payment security and fraud prevention.
              </p>
            </PolicySection>

            <PolicySection title="International transfers">
              <p>
                Google and Stripe may process information outside the UK, including in the United States.
                Where a provider transfers personal data internationally, UK data-protection rules require
                an approved safeguard such as an adequacy regulation or contractual protections.
              </p>
              <p>
                For current information about locations and transfer safeguards, read{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 underline"
                >
                  Google&apos;s privacy policy
                </a>{' '}
                and{' '}
                <a
                  href="https://stripe.com/gb/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 underline"
                >
                  Stripe&apos;s privacy policy
                </a>.
              </p>
            </PolicySection>

            <PolicySection title="Managing browser storage">
              <p>
                You can also inspect, block or delete cookies and local storage in your browser
                settings. Clearing local storage removes your saved choice, so Fixtyer will ask again
                on your next visit. Blocking essential storage may stop account and claim-preparation
                features from working.
              </p>
            </PolicySection>

            <PolicySection title="Who is responsible">
              <p>
                Fixtyer is an ICO-listed trading name of Elevyn Technology Group Limited (company
                number 16954601), registered office 167-169 Great Portland Street, London, England,
                W1W 5PF. ICO registration: ZC078522.
              </p>
              <p>
                For questions about this policy, email{' '}
                <a href="mailto:privacy@fixtyer.co.uk" className="text-brand-600 underline">
                  privacy@fixtyer.co.uk
                </a>. For broader information about personal data, read our{' '}
                <Link to="/privacy" className="text-brand-600 underline">Privacy Policy</Link>.
              </p>
            </PolicySection>
          </div>
        </div>
      </section>
    </div>
  );
}

function PolicySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-semibold text-brand-950">{title}</h2>
      <div className="mt-4 space-y-4 leading-relaxed">{children}</div>
    </section>
  );
}
